import React, { useEffect, useState, useCallback } from "react";
import { X, Plus, Trash2, Target, Lock } from "lucide-react";
import { api, fmtINR, formatApiError } from "../../lib/api";
import { Checkbox } from "../ui/checkbox";
import MoneyInput from "../ui/MoneyInput";
import { toast } from "sonner";

const BAND_STYLES = {
  "On Track":
    "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  "Slightly Behind":
    "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300",
  "At Risk": "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300",
};
const barFor = (b) =>
  b === "On Track"
    ? "bg-emerald-500"
    : b === "Slightly Behind"
      ? "bg-amber-500"
      : "bg-rose-500";

export default function GoalFundingModal({ goal, onClose, onChanged }) {
  const [projection, setProjection] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);

  // add form
  const [assetId, setAssetId] = useState("");
  const [mode, setMode] = useState("percent"); // percent | amount
  const [val, setVal] = useState("");
  const [applyContrib, setApplyContrib] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, nw] = await Promise.all([
        api.get(`/goals/${goal.id}/allocations`),
        api.get("/planning/net-worth"),
      ]);
      setProjection(p.data.projection);
      setReservation(nw.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [goal.id]);

  useEffect(() => {
    load();
  }, [load]);

  const assetMap = {};
  (reservation?.assets || []).forEach((a) => (assetMap[a.asset_id] = a));
  // assets that still have room to allocate (insurance cash-value rows are
  // read-only synthetic entries — they can't be reserved for a goal).
  const availableAssets = (reservation?.assets || []).filter(
    (a) => a.available_percent > 0.01 && !a.synthetic,
  );

  const addAllocation = async () => {
    if (!assetId) return toast.error("Pick an asset");
    if (!val) return toast.error("Enter a value");
    setSaving(true);
    try {
      const payload = {
        asset_id: assetId,
        apply_to_contributions: applyContrib,
      };
      if (mode === "percent") payload.allocation_percent = Number(val);
      else payload.amount = Number(String(val).replace(/,/g, ""));
      await api.post(`/goals/${goal.id}/allocations`, payload);
      toast.success("Asset reserved for this goal");
      setAssetId("");
      setVal("");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeAllocation = async (allocationId) => {
    try {
      await api.delete(`/allocations/${allocationId}`);
      toast.success("Reservation removed");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const selected = assetMap[assetId];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto p-4">
      <div className="min-h-full flex items-center justify-center py-10">
        <div className="w-full max-w-2xl card-soft p-6">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">
                Goal funding
              </div>
              <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
                Reserve assets · {goal.name}
              </h2>
            </div>
            <button
              data-testid="funding-modal-close"
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {loading ? (
            <div className="py-12 text-center text-sm text-slate-500">
              Loading…
            </div>
          ) : (
            <>
              {/* Funding summary */}
              {projection && (
                <div className="mt-4 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Funding status
                      </span>
                    </div>
                    <span
                      data-testid="funding-band"
                      className={`text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full ${BAND_STYLES[projection.funding_band]}`}
                    >
                      {projection.funding_band}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    <Mini
                      label="Required (future)"
                      value={fmtINR(projection.future_requirement, {
                        compact: true,
                      })}
                    />
                    <Mini
                      label="Projected"
                      value={fmtINR(projection.projected_value, {
                        compact: true,
                      })}
                      accent="text-emerald-600"
                    />
                    <Mini
                      label={projection.funding_gap > 0 ? "Gap" : "Surplus"}
                      value={fmtINR(Math.abs(projection.funding_gap), {
                        compact: true,
                      })}
                      accent={
                        projection.funding_gap > 0
                          ? "text-rose-600"
                          : "text-emerald-600"
                      }
                    />
                    <Mini
                      label="Reserved today"
                      value={fmtINR(projection.reserved_today, {
                        compact: true,
                      })}
                    />
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full ${barFor(projection.funding_band)}`}
                      style={{
                        width: `${Math.min(projection.funding_pct, 100)}%`,
                      }}
                    />
                  </div>
                  <div className="mt-1.5 flex justify-between text-[11px] text-slate-500">
                    <span>
                      Target {fmtINR(projection.target_today)} today · inflated
                      at {projection.inflation_pct}% over{" "}
                      {projection.years_to_target} yrs
                    </span>
                    <span>{Math.min(projection.funding_pct, 999)}%</span>
                  </div>
                  {projection.funding_gap > 0 &&
                    projection.additional_monthly_investment > 0 && (
                      <p className="mt-2 text-xs text-slate-500">
                        Invest about{" "}
                        <span className="font-semibold text-slate-700 dark:text-slate-200">
                          {fmtINR(projection.additional_monthly_investment)}/mo
                        </span>{" "}
                        more (or reserve additional assets) to get on track.
                      </p>
                    )}
                </div>
              )}

              {/* Current reservations */}
              <div className="mt-5">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                  Reserved assets
                </div>
                {projection?.assets?.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500 text-center">
                    No assets reserved yet. Add one below — only reserved assets
                    fund this goal.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {projection?.assets?.map((a) => (
                      <div
                        key={a.allocation_id || a.asset_id}
                        data-testid={`alloc-row-${a.asset_id}`}
                        className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5"
                      >
                        <div>
                          <div className="text-sm font-semibold text-slate-900 dark:text-white">
                            {a.asset_name}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {a.allocation_percent}% reserved ·{" "}
                            {fmtINR(a.reserved_today)} today →{" "}
                            {fmtINR(a.projected_value, { compact: true })}{" "}
                            projected
                          </div>
                        </div>
                        <button
                          data-testid={`remove-alloc-${a.asset_id}`}
                          onClick={() => removeAllocation(a.allocation_id)}
                          className="p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-500/10 text-slate-500 hover:text-rose-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add reservation */}
              <div className="mt-5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
                  Reserve another asset
                </div>
                {availableAssets.length === 0 ? (
                  <p className="text-sm text-slate-500 flex items-center gap-2">
                    <Lock className="h-4 w-4" /> All your assets are fully
                    reserved. Free up an asset or add a new one.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <select
                      data-testid="funding-asset-select"
                      value={assetId}
                      onChange={(e) => setAssetId(e.target.value)}
                      className="input-soft"
                    >
                      <option value="">Select an asset…</option>
                      {availableAssets.map((a) => (
                        <option key={a.asset_id} value={a.asset_id}>
                          {a.asset_name} — {a.available_percent}% free (
                          {fmtINR(a.available_value, { compact: true })})
                        </option>
                      ))}
                    </select>

                    <div className="flex items-center gap-2">
                      <div className="flex rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-0.5">
                        {["percent", "amount"].map((m) => (
                          <button
                            key={m}
                            onClick={() => setMode(m)}
                            className={`px-3 py-1 text-xs font-semibold rounded-md capitalize ${
                              mode === m
                                ? "bg-emerald-500 text-white"
                                : "text-slate-500"
                            }`}
                          >
                            {m === "percent" ? "%" : "₹"}
                          </button>
                        ))}
                      </div>
                      {mode === "amount" ? (
                        <div className="flex-1">
                          <MoneyInput
                            data-testid="funding-value-input"
                            value={val}
                            onValueChange={(raw) => setVal(raw)}
                            placeholder="e.g. 5,00,000"
                          />
                        </div>
                      ) : (
                        <input
                          data-testid="funding-value-input"
                          type="text"
                          value={val}
                          onChange={(e) => setVal(e.target.value)}
                          className="input-soft flex-1"
                          placeholder="e.g. 50"
                        />
                      )}
                    </div>

                    {selected && (
                      <p className="text-[11px] text-slate-500">
                        {selected.asset_name}: {selected.available_percent}% (
                        {fmtINR(selected.available_value)}) available to
                        reserve.
                      </p>
                    )}

                    <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                      <Checkbox
                        checked={applyContrib}
                        onCheckedChange={(checked) =>
                          setApplyContrib(checked === true)
                        }
                      />

                      <span>
                        Also reserve future monthly contributions to this asset
                      </span>
                    </label>

                    <button
                      data-testid="funding-add-btn"
                      onClick={addAllocation}
                      disabled={saving}
                      className="btn-primary w-full !text-xs"
                    >
                      <Plus className="h-3.5 w-3.5" />{" "}
                      {saving ? "Reserving…" : "Reserve for goal"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, accent = "text-slate-900 dark:text-white" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className={`num-mono text-sm font-bold mt-0.5 ${accent}`}>
        {value}
      </div>
    </div>
  );
}
